// MedSnapp's cast (3 Nov 2026): the 47 organ characters from MedSnapp's own website repo
// (brand/mascots/<name>/<name>-<version>.svg, the first version listed per character), served from web/public/cast as
// static files, so the player's bundle carries only this list, never the drawings. Not ours to redraw.
//
// mascotFor maps a question's category to its organ character. Every category in content/pool/pool.json has an exact
// entry (cast.test.ts checks it); a category added later falls through the keyword rules, then to Beat.

export interface CastMember {
  /** The file name and the id: /cast/<name>.svg. */
  name: string;
  character: string;
  organ: string;
  system: string;
}

export const CAST: readonly CastMember[] = [
  { name: 'beat', character: 'Beat', organ: 'Heart', system: 'Cardiology' },
  { name: 'lobe', character: 'Lobe', organ: 'Brain', system: 'Neurology' },
  { name: 'puff', character: 'Puff', organ: 'Lungs', system: 'Respiratory' },
  { name: 'bean', character: 'Bean', organ: 'Kidney', system: 'Renal' },
  { name: 'rubi', character: 'Rubi', organ: 'Red blood cell', system: 'Haematology' },
  { name: 'liv', character: 'Liv', organ: 'Liver', system: 'Hepatology' },
  { name: 'gus', character: 'Gus', organ: 'Stomach', system: 'Gastroenterology' },
  { name: 'femi', character: 'Femi', organ: 'Bone', system: 'Musculoskeletal' },
  { name: 'thyra', character: 'Thyra', organ: 'Thyroid', system: 'Endocrinology' },
  { name: 'steth', character: 'Steth', organ: 'Stethoscope', system: 'Clinical skills' },
  { name: 'bleep', character: 'Bleep', organ: 'Pager', system: 'On call' },
  { name: 'oz', character: 'Oz', organ: 'Oesophagus', system: 'Gastroenterology' },
  { name: 'villi', character: 'Villi', organ: 'Small intestine', system: 'Gastroenterology' },
  { name: 'colin', character: 'Colin', organ: 'Large intestine', system: 'Gastroenterology' },
  { name: 'appy', character: 'Appy', organ: 'Appendix', system: 'Gastroenterology' },
  { name: 'isla', character: 'Isla', organ: 'Pancreas', system: 'Gastroenterology' },
  { name: 'bili', character: 'Bili', organ: 'Gallbladder', system: 'Hepatobiliary' },
  { name: 'iris', character: 'Iris', organ: 'Eye', system: 'Ophthalmology' },
  { name: 'echo', character: 'Echo', organ: 'Ear', system: 'ENT' },
  { name: 'sniff', character: 'Sniff', organ: 'Nose', system: 'ENT' },
  { name: 'tang', character: 'Tang', organ: 'Tongue', system: 'ENT' },
  { name: 'molly', character: 'Molly', organ: 'Tooth', system: 'Dentistry' },
  { name: 'derma', character: 'Derma', organ: 'Skin', system: 'Dermatology' },
  { name: 'addie', character: 'Addie', organ: 'Adrenal gland', system: 'Endocrinology' },
  { name: 'pitt', character: 'Pitt', organ: 'Pituitary gland', system: 'Endocrinology' },
  { name: 'len', character: 'Len', organ: 'Spleen', system: 'Haematology' },
  { name: 'nodo', character: 'Nodo', organ: 'Lymph node', system: 'Immunology' },
  { name: 'tim', character: 'Tim', organ: 'Thymus', system: 'Immunology' },
  { name: 'artie', character: 'Artie', organ: 'Artery and vein', system: 'Vascular' },
  { name: 'vesi', character: 'Vesi', organ: 'Bladder', system: 'Urology' },
  { name: 'uma', character: 'Uma', organ: 'Uterus', system: 'Obstetrics and gynaecology' },
  { name: 'ova', character: 'Ova', organ: 'Ovary', system: 'Obstetrics and gynaecology' },
  { name: 'teo', character: 'Teo', organ: 'Testis', system: 'Urology' },
  { name: 'walt', character: 'Walt', organ: 'Prostate', system: 'Urology' },
  { name: 'placi', character: 'Placi', organ: 'Placenta', system: 'Obstetrics and gynaecology' },
  { name: 'cora', character: 'Cora', organ: 'Spinal cord', system: 'Neurology' },
  { name: 'flex', character: 'Flex', organ: 'Skeletal muscle', system: 'Musculoskeletal' },
  { name: 'lary', character: 'Lary', organ: 'Larynx', system: 'ENT' },
  { name: 'dia', character: 'Dia', organ: 'Diaphragm', system: 'Respiratory' },
  { name: 'bella', character: 'Bella', organ: 'Cerebellum', system: 'Neurology' },
  { name: 'hal', character: 'Hal', organ: 'Hypothalamus', system: 'Endocrinology' },
  { name: 'pina', character: 'Pina', organ: 'Pineal gland', system: 'Endocrinology' },
  { name: 'fay', character: 'Fay', organ: 'Pharynx', system: 'ENT' },
  { name: 'mae', character: 'Mae', organ: 'Breast', system: 'Breast surgery' },
  { name: 'sal', character: 'Sal', organ: 'Salivary glands', system: 'ENT' },
  { name: 'rex', character: 'Rex', organ: 'Rectum and anal canal', system: 'Colorectal surgery' },
  { name: 'jo', character: 'Jo', organ: 'Knee joint', system: 'Orthopaedics' },
];

/** The character every unknown category gets, and the one crowned at the podium. */
export const DEFAULT_CAST = 'beat';
/** The cast in the lobby's parade, Bleep first: it starts every battle. */
export const LOBBY_PARADE: readonly string[] = ['bleep', 'beat', 'lobe', 'puff'];

const BY_NAME = new Map(CAST.map((c) => [c.name, c]));
export const castMember = (name: string): CastMember | undefined => BY_NAME.get(name);
export const isCastName = (name: string): boolean => BY_NAME.has(name);

/** Bump when the files in web/public/cast change. */
export const CAST_VERSION = 1;
/** The file for a cast member: the full drawing, or the simplified glyph for small sizes. */
export const castSrc = (name: string, glyph = false) => `/cast/${name}${glyph ? '-glyph' : ''}.svg?v=${CAST_VERSION}`;

/** Every category in the pool (clinical and Year 1–2), lower case, to its organ character. */
const EXACT: Readonly<Record<string, string>> = {
  // Clinical
  'anaesthetics & peri-operative care': 'lary',
  breast: 'mae',
  cardiology: 'beat',
  cardiothoracic: 'beat',
  colorectal: 'colin',
  dermatology: 'derma',
  'emergency medicine': 'bleep',
  endocrinology: 'thyra',
  gastroenterology: 'gus',
  'general practice': 'steth',
  'general surgery': 'appy',
  'genitourinary medicine': 'vesi',
  'geriatric medicine': 'femi',
  haematology: 'rubi',
  'infectious diseases': 'nodo',
  neurology: 'lobe',
  neurosurgery: 'cora',
  'obstetrics & gynaecology': 'uma',
  oncology: 'nodo',
  ophthalmology: 'iris',
  paediatrics: 'tim',
  'palliative care': 'steth',
  pharmacology: 'liv',
  'plastic surgery': 'derma',
  psychiatry: 'lobe',
  'renal medicine': 'bean',
  'respiratory medicine': 'puff',
  rheumatology: 'jo',
  'trauma & orthopaedics': 'femi',
  'upper gi and hepatobiliary': 'liv',
  urology: 'vesi',
  vascular: 'artie',
  // Year 1–2
  'blood, immunity and infection': 'rubi',
  cardiovascular: 'beat',
  endocrine: 'thyra',
  'foundation sciences': 'beat',
  'gastrointestinal and liver': 'gus',
  'musculoskeletal and skin': 'femi',
  'nervous system': 'lobe',
  'renal and urinary': 'bean',
  reproduction: 'ova',
  respiratory: 'puff',
};

/** For a category the list above does not know: the first rule whose words appear in it. */
const RULES: ReadonlyArray<[RegExp, string]> = [
  [/cardi|heart/, 'beat'],
  [/respir|lung|thorac|chest/, 'puff'],
  [/neuro|nerv|brain/, 'lobe'],
  [/renal|nephro|kidney/, 'bean'],
  [/haemat|hemat|blood/, 'rubi'],
  [/hepat|liver/, 'liv'],
  [/gastro|\bgi\b|bowel|gut/, 'gus'],
  [/endocr|diabet|thyroid/, 'thyra'],
  [/musculo|rheumat|orthop|bone|joint/, 'femi'],
  [/ophthal|\beye/, 'iris'],
  [/\bent\b|ear|otolaryng/, 'echo'],
  [/dermat|skin/, 'derma'],
  [/obstet|gynae|gynec|matern/, 'uma'],
  [/urolog|bladder/, 'vesi'],
  [/infect|immun|microb/, 'nodo'],
  [/vascul|arter|vein/, 'artie'],
  [/emergenc|acute|resus/, 'bleep'],
  [/clinical skills|history|examination/, 'steth'],
];

/**
 * The cast member for a question's category: its organ character, Beat when there is no category or nothing matches.
 * `stage` (the level, 'clinical' or 'preclinical') is accepted for callers that have it; the map covers both levels.
 */
export function mascotFor(category?: string, _stage?: string): string {
  const c = (category ?? '').trim().toLowerCase();
  if (!c) return DEFAULT_CAST;
  const exact = EXACT[c];
  if (exact) return exact;
  for (const [re, name] of RULES) if (re.test(c)) return name;
  return DEFAULT_CAST;
}

/** The exact map, for the test that every pool category is covered. */
export const CATEGORY_CAST: Readonly<Record<string, string>> = EXACT;
